import { Archive, Check, House, MessageSquareHeart, Quote, Star, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Chip, KpiTile, Monogram, Note, Panel, Tabs, Track } from '@/components/redesign/bits'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { Textarea } from '@/components/ui/textarea'
import { PageTitle } from '@/app/layout'
import { errorMessage, isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/cn'
import { formatDate, formatRelative, numberFmt } from '@/lib/format'
import {
  type AssistantRating,
  type Feedback,
  type FeedbackList,
  useFeedback,
  useFeedbackHomepage,
  useUpdateFeedback,
} from './api'

type View = 'new' | 'all' | 'quotable' | 'homepage' | 'archived' | 'assistant'

/**
 * Customers' feedback in one place (owner, 2026-10-22): what they rated and wrote, asked in the
 * app at a good moment or given from its menu. Read it, archive it, keep a note, and (owners and
 * admins) quote it on the homepage when the person agreed, in their own words.
 */
export function FeedbackPage() {
  const feedback = useFeedback()

  return (
    <>
      <PageTitle
        title="Feedback"
        description="What customers rated and wrote, from the app’s feedback note and its Give feedback menu, and their thumbs up or down on Arva Assistant’s replies. With their consent, owners and admins can quote feedback on the homepage: it shows there within minutes."
      />
      {feedback.isPending ? (
        <Skeleton className="h-96 rounded-[14px]" />
      ) : feedback.isError ? (
        <div className="card">
          <ErrorState error={feedback.error} onRetry={() => void feedback.refetch()} />
        </div>
      ) : (
        <FeedbackBoard list={feedback.data} />
      )}
    </>
  )
}

function FeedbackBoard({ list }: { list: FeedbackList }) {
  const [params] = useSearchParams()
  const focus = params.get('id')
  const s = list.meta.summary
  const ratings = list.assistant ?? []
  // All first, so a message doesn't vanish from the list the moment it's read or quoted. A thumbs-down
  // notification links to the Arva Assistant tab (?tab=assistant).
  const [view, setView] = useState<View>(params.get('tab') === 'assistant' ? 'assistant' : 'all')
  const items = useMemo(
    () =>
      list.data.filter((f) =>
        view === 'new'
          ? f.status === 'new'
          : view === 'quotable'
            ? f.blocker === null && f.status !== 'archived'
            : view === 'homepage'
              ? f.on_homepage
              : view === 'archived'
                ? f.status === 'archived'
                : f.status !== 'archived',
      ),
    [list.data, view],
  )
  const count = (v: View) =>
    list.data.filter((f) =>
      v === 'quotable'
        ? f.blocker === null && f.status !== 'archived'
        : v === 'homepage'
          ? f.on_homepage
          : v === 'archived'
            ? f.status === 'archived'
            : v === 'new'
              ? f.status === 'new'
              : f.status !== 'archived',
    ).length

  useEffect(() => {
    if (focus) document.getElementById(`feedback-${focus}`)?.scrollIntoView({ block: 'center' })
  }, [focus])

  return (
    <div className="sec" style={{ gap: 20 }}>
      <div className="kpis">
        <KpiTile
          label="Average rating"
          value={s.average !== null ? s.average.toFixed(1) : '–'}
          unit="/ 5"
          compare={`from ${numberFmt.format(s.total)} ${s.total === 1 ? 'rating' : 'ratings'} (the team’s own accounts left out)`}
        />
        <KpiTile
          label="Last 30 days"
          value={numberFmt.format(s.last_30_days)}
          compare={s.average_30_days !== null ? `averaging ${s.average_30_days.toFixed(1)}` : 'none yet'}
        />
        <KpiTile label="New to read" value={numberFmt.format(s.new)} compare="not opened by the team yet" />
        <KpiTile
          label="On the homepage"
          value={numberFmt.format(s.on_homepage)}
          compare={`${numberFmt.format(s.may_quote)} may be quoted`}
        />
      </div>

      <div className="split">
        <Panel title="Ratings" sub="How many of each, from customers">
          <div className="grid gap-2.5">
            {(['5', '4', '3', '2', '1'] as const).map((r) => (
              <div key={r} className="grid grid-cols-[52px_1fr_40px] items-center gap-3 text-[13px]">
                <span className="flex items-center gap-1 font-semibold">
                  {r} <Star className="size-3.5 fill-current text-warning" aria-hidden="true" />
                </span>
                <Track
                  ratio={s.total > 0 ? s.by_rating[r] / s.total : 0}
                  tone={Number(r) <= 2 ? 'bad' : Number(r) === 3 ? 'warn' : ''}
                />
                <span className="text-right tabular-nums">{numberFmt.format(s.by_rating[r])}</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="On the homepage" sub="Only with the person’s consent, in their own words">
          <ul className="grid gap-2 text-[13px] text-ink-muted">
            <li>Pick feedback under Can be quoted, then Show on the homepage.</li>
            <li>
              Show all of it, or a part: their words in order, with … for anything left out (up to{' '}
              {list.meta.max_quote} characters).
            </li>
            <li>
              The section on arvaseo.com shows up to 12, lowest position number first, and hides when there
              are none.
            </li>
            <li>
              The team’s own and test accounts can’t be quoted, and a closed account’s quote comes down by
              itself.
            </li>
          </ul>
        </Panel>
      </div>

      <Tabs<View>
        label="Which feedback"
        value={view}
        onChange={setView}
        tabs={[
          { key: 'new', label: 'New', count: count('new'), alert: count('new') > 0 },
          { key: 'all', label: 'All', count: count('all') },
          { key: 'quotable', label: 'Can be quoted', count: count('quotable') },
          { key: 'homepage', label: 'On the homepage', count: count('homepage') },
          { key: 'archived', label: 'Archived', count: count('archived') },
          { key: 'assistant', label: 'Arva Assistant', count: ratings.length },
        ]}
      />

      {view === 'assistant' ? (
        <AssistantRatings ratings={ratings} summary={list.meta.assistant_summary} focus={focus} />
      ) : items.length === 0 ? (
        <Note icon={<MessageSquareHeart aria-hidden="true" />} title="Nothing here" calm>
          {list.data.length === 0
            ? 'No feedback yet. The app asks people 14 days in, after something has gone right, and anyone can give it from the account menu.'
            : 'Nothing in this list right now.'}
        </Note>
      ) : (
        <div className="grid gap-3">
          {items.map((f) => (
            <FeedbackCard key={f.id} item={f} canPublish={list.meta.can_publish} focused={f.id === focus} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Thumbs up and down on Arva Assistant's replies: what was rated, where, and what the person said. */
function AssistantRatings({
  ratings,
  summary,
  focus,
}: {
  ratings: AssistantRating[]
  summary: FeedbackList['meta']['assistant_summary']
  /** The rating a thumbs-down notification links to. */
  focus: string | null
}) {
  return (
    <div className="grid gap-3">
      {summary && summary.total > 0 && (
        <p className="text-[13px] text-ink-muted">
          {numberFmt.format(summary.up)} helpful and {numberFmt.format(summary.down)} not helpful in all;{' '}
          {numberFmt.format(summary.down_30_days)} of the last 30 days’{' '}
          {numberFmt.format(summary.last_30_days)}{' '}
          {summary.last_30_days === 1 ? 'rating was' : 'ratings were'} not helpful. The team’s own accounts
          are left out.
        </p>
      )}
      {ratings.length === 0 ? (
        <Note icon={<MessageSquareHeart aria-hidden="true" />} title="Nothing here" calm>
          No ratings yet. People rate Arva’s replies with the thumbs under each one.
        </Note>
      ) : (
        ratings.map((r) => <AssistantRatingCard key={r.id} item={r} focused={r.id === focus} />)
      )}
    </div>
  )
}

function AssistantRatingCard({ item, focused }: { item: AssistantRating; focused: boolean }) {
  const up = item.rating === 'up'
  return (
    <article
      id={`feedback-${item.id}`}
      className={cn('card pad grid gap-3', focused && 'ring-2 ring-[var(--focus)]')}
      aria-label={`Arva Assistant rating from ${item.person.name}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="who">
          <Monogram name={item.person.name} size="sm" />
          <div>
            <b>
              {item.person.name}
              {item.person.closed && <span className="font-normal text-ink-subtle"> (account closed)</span>}
            </b>
            <small>
              {item.person.email}
              {item.workspace && (
                <>
                  {' · '}
                  <Link to={`/customers/${item.workspace.id}`}>{item.workspace.name}</Link>
                </>
              )}
            </small>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone={up ? 'ok' : 'bad'}>
            {up ? <ThumbsUp aria-hidden="true" /> : <ThumbsDown aria-hidden="true" />}
            {up ? 'Helpful' : 'Not helpful'}
          </Chip>
          <span className="text-xs text-ink-subtle" title={formatDate(item.updated_at ?? item.created_at)}>
            {formatRelative(item.updated_at ?? item.created_at)}
          </span>
        </div>
      </div>

      <figure className="grid gap-1">
        <figcaption className="text-xs text-ink-subtle">
          Arva’s reply on <b className="font-semibold text-ink">{item.website.domain}</b>
        </figcaption>
        <blockquote className="rounded-md bg-surface-sunken px-3 py-2 text-[13.5px] leading-relaxed whitespace-pre-line text-ink">
          {item.reply || 'The reply has no text.'}
        </blockquote>
      </figure>

      {item.comment ? (
        <p className="text-[14.5px] leading-relaxed whitespace-pre-line text-ink">{item.comment}</p>
      ) : (
        <p className="text-[13px] text-ink-subtle">A rating, with nothing written.</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Chip tone="line">{item.source}</Chip>
        {item.person.internal && <Chip tone="warn">Team or test account</Chip>}
      </div>
    </article>
  )
}

function Rating({ value }: { value: number }) {
  return (
    <span className="flex items-center gap-0.5" role="img" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={cn('size-4', n <= value ? 'fill-current text-warning' : 'text-border-strong')}
          aria-hidden="true"
        />
      ))}
    </span>
  )
}

function FeedbackCard({
  item,
  canPublish,
  focused,
}: {
  item: Feedback
  canPublish: boolean
  focused: boolean
}) {
  const update = useUpdateFeedback()
  const [noteOpen, setNoteOpen] = useState(false)
  const [note, setNote] = useState(item.note ?? '')
  const fail = (e: unknown) => toast.error(errorMessage(e))

  return (
    <article
      id={`feedback-${item.id}`}
      className={cn('card pad grid gap-3', focused && 'ring-2 ring-[var(--focus)]')}
      aria-label={`Feedback from ${item.person.name}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="who">
          <Monogram name={item.person.name} size="sm" />
          <div>
            <b>
              {item.person.name}
              {item.person.closed && <span className="font-normal text-ink-subtle"> (account closed)</span>}
            </b>
            <small>
              {item.person.email}
              {item.workspace && (
                <>
                  {' · '}
                  <Link to={`/customers/${item.workspace.id}`}>{item.workspace.name}</Link>
                </>
              )}
            </small>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Rating value={item.rating} />
          <span className="text-xs text-ink-subtle" title={formatDate(item.created_at)}>
            {formatRelative(item.created_at)}
          </span>
        </div>
      </div>

      {item.message ? (
        <p className="text-[14.5px] leading-relaxed whitespace-pre-line text-ink">{item.message}</p>
      ) : (
        <p className="text-[13px] text-ink-subtle">A rating, with nothing written.</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Chip tone="line">{item.source === 'prompt' ? 'Asked in the app' : 'From the menu'}</Chip>
        {item.status === 'new' && <Chip tone="lime">New</Chip>}
        {item.status === 'archived' && <Chip>Archived</Chip>}
        {item.person.internal && <Chip tone="warn">Team or test account</Chip>}
        {item.may_quote ? (
          <Chip tone="ok">
            May be quoted as {item.display_name}
            {item.display_title ? `, ${item.display_title}` : ''}
          </Chip>
        ) : (
          <Chip>Not for quoting</Chip>
        )}
        {item.on_homepage && (
          <Chip tone="ink">
            On the homepage{item.homepage_position !== null ? ` · position ${item.homepage_position}` : ''}
          </Chip>
        )}
      </div>

      {item.note && !noteOpen && (
        <p className="rounded-md bg-surface-sunken px-3 py-2 text-[13px] text-ink-muted">
          <b className="text-ink">Note:</b> {item.note}
        </p>
      )}
      {noteOpen && (
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            update.mutate(
              { id: item.id, note: note.trim() || null },
              { onSuccess: () => setNoteOpen(false), onError: fail },
            )
          }}
        >
          <Field label="Team note" htmlFor={`note-${item.id}`} hint="Only the team sees it.">
            <Textarea
              id={`note-${item.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              rows={2}
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={update.isPending}>
              Save note
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setNoteOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {item.status === 'new' && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => update.mutate({ id: item.id, status: 'read' }, { onError: fail })}
          >
            <Check aria-hidden="true" /> Mark as read
          </Button>
        )}
        {item.status !== 'archived' ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => update.mutate({ id: item.id, status: 'archived' }, { onError: fail })}
          >
            <Archive aria-hidden="true" /> Archive
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => update.mutate({ id: item.id, status: 'read' }, { onError: fail })}
          >
            Bring back
          </Button>
        )}
        {!noteOpen && (
          <Button size="sm" variant="ghost" onClick={() => setNoteOpen(true)}>
            {item.note ? 'Edit note' : 'Add a note'}
          </Button>
        )}
      </div>

      <HomepageControls item={item} canPublish={canPublish} />
    </article>
  )
}

/** Show it on the homepage (owners and admins), with the part to show and its place. */
function HomepageControls({ item, canPublish }: { item: Feedback; canPublish: boolean }) {
  const homepage = useFeedbackHomepage()
  const [editing, setEditing] = useState(false)
  const [quote, setQuote] = useState(item.homepage_quote ?? item.message ?? '')
  const [position, setPosition] = useState(item.homepage_position?.toString() ?? '')
  const quoteError = isApiError(homepage.error) ? homepage.error.firstFieldError('quote') : undefined
  const general = homepage.error && !quoteError ? errorMessage(homepage.error) : null

  if (item.blocker !== null && !item.on_homepage) {
    return (
      <p className="flex items-center gap-2 border-t border-border pt-3 text-[13px] text-ink-subtle">
        <House className="size-4" aria-hidden="true" /> Can’t go on the homepage: {item.blocker}
      </p>
    )
  }
  if (!canPublish) {
    return item.on_homepage ? (
      <p className="border-t border-border pt-3 text-[13px] text-ink-muted">
        Shown on the homepage since {formatDate(item.published_at)}
        {item.published_by ? ` (by ${item.published_by})` : ''}. Owners and admins change it.
      </p>
    ) : null
  }

  const save = (on: boolean) =>
    homepage.mutate(
      {
        id: item.id,
        on_homepage: on,
        quote: quote.trim() === (item.message ?? '').trim() ? null : quote,
        position: position.trim() === '' ? null : Number(position),
      },
      {
        onSuccess: () => {
          setEditing(false)
          toast.success(
            on ? 'On the homepage. It shows on arvaseo.com within a few minutes.' : 'Taken off the homepage.',
          )
        },
      },
    )

  return (
    <div className="grid gap-3 border-t border-border pt-3">
      {item.on_homepage && !editing && (
        <figure className="grid gap-1 rounded-md bg-surface-sunken px-3 py-2">
          <blockquote className="text-[13.5px] text-ink">“{item.shown_quote}”</blockquote>
          <figcaption className="text-xs text-ink-subtle">
            {item.display_name}
            {item.display_title ? `, ${item.display_title}` : ''} · on the homepage since{' '}
            {formatDate(item.published_at)}
            {item.published_by ? ` by ${item.published_by}` : ''}
          </figcaption>
        </figure>
      )}
      {editing && (
        <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
          <Field
            label="What the homepage shows"
            htmlFor={`quote-${item.id}`}
            error={quoteError}
            hint="Their words in order. Leave parts out with …, but never change or add words."
          >
            <Textarea
              id={`quote-${item.id}`}
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              rows={3}
              aria-invalid={quoteError ? true : undefined}
              aria-describedby={quoteError ? `quote-${item.id}-error` : `quote-${item.id}-hint`}
            />
          </Field>
          <Field label="Position" htmlFor={`position-${item.id}`} hint="1 shows first">
            <Input
              id={`position-${item.id}`}
              inputMode="numeric"
              value={position}
              onChange={(e) => setPosition(e.target.value.replace(/\D/g, '').slice(0, 3))}
              aria-describedby={`position-${item.id}-hint`}
            />
          </Field>
        </div>
      )}
      {general && (
        <p role="alert" className="text-sm text-danger">
          {general}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {editing ? (
          <>
            <Button size="sm" onClick={() => save(true)} disabled={homepage.isPending}>
              <Quote aria-hidden="true" /> {item.on_homepage ? 'Save' : 'Show on the homepage'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </>
        ) : item.on_homepage ? (
          <>
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
              Change the quote
            </Button>
            <Button size="sm" variant="ghost" onClick={() => save(false)} disabled={homepage.isPending}>
              Take it off the homepage
            </Button>
          </>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            <House aria-hidden="true" /> Show on the homepage…
          </Button>
        )}
      </div>
    </div>
  )
}
